import { useCallback, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { adminApi } from "@/components/Admin/api";
import { useAdminData } from "@/components/Admin/hooks";
import StatsRow from "@/components/Admin/StatsRow";
import SubmissionsTable from "@/components/Admin/SubmissionsTable";
import EmailPanel from "@/components/Admin/EmailPanel";
import WebhookPanel from "@/components/Admin/WebhookPanel";

export default function Dashboard({ onLogout }) {
    const [filter, setFilter] = useState("all");
    const {
        stats,
        rows,
        webhookLog,
        emailLog,
        loading,
        refresh,
        setStats,
        setEmailLog,
        setWebhookLog,
    } = useAdminData(true, filter);

    const refreshLogs = useCallback(async () => {
        const [s, el, wl] = await Promise.all([
            adminApi.get("/stats"),
            adminApi.get("/email/log", { params: { limit: 25 } }),
            adminApi.get("/webhook/log", { params: { limit: 25 } }),
        ]);
        setStats(s.data);
        setEmailLog(el.data || []);
        setWebhookLog(wl.data || []);
    }, [setStats, setEmailLog, setWebhookLog]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white">
            <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-3">
                        <img
                            src="/edara-logo.png"
                            alt=""
                            className="w-12 h-12 object-contain"
                        />
                        <span className="font-display text-2xl tracking-wider">
                            EDARA{" "}
                            <span className="text-[#1FA8FF]">SOFTWARE</span>
                        </span>
                        <span className="hidden sm:inline ml-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-l border-white/15 pl-3">
                            Admin
                        </span>
                    </a>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={refresh}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm"
                            data-testid="admin-refresh"
                        >
                            <RefreshCw
                                className={`w-4 h-4 ${
                                    loading ? "animate-spin" : ""
                                }`}
                            />
                            Refresh
                        </button>
                        <button
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 hover:bg-white/5 text-sm"
                            data-testid="admin-signout"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <StatsRow
                    stats={stats}
                    webhookLog={webhookLog}
                    emailLog={emailLog}
                />
                <SubmissionsTable
                    rows={rows}
                    loading={loading}
                    filter={filter}
                    onFilter={setFilter}
                />
                <EmailPanel
                    stats={stats}
                    emailLog={emailLog}
                    onAfterTest={refreshLogs}
                />
                <WebhookPanel
                    webhookLog={webhookLog}
                    onAfterTest={refreshLogs}
                />
            </main>
        </div>
    );
}
